import React, { useState, useEffect } from 'react';
import { Contact } from '../../types/contact';
import { EngagementWithDetails, ContactStepStatus } from '../../types/engagement';
import ContactCard from '../contacts/ContactCard';
import { supabase } from '../../lib/supabase';

interface ExecutingEngagementViewProps {
  engagement: EngagementWithDetails;
  onMoveContact: (contactId: string, fromStepId: string | null, toStepId: string | null) => Promise<EngagementWithDetails>;
}

const renderContactCard = (contact: Contact) => (
  <div className="bg-dark-800 p-3 rounded">
    <div className="text-sm font-medium text-white">{contact.name}</div>
    <div className="text-xs text-gray-400 truncate w-full overflow-hidden">
      {contact.email}
    </div>
  </div>
);

export default function ExecutingEngagementView({ 
  engagement,
  onMoveContact
}: ExecutingEngagementViewProps) {
  const [contactsMap, setContactsMap] = useState<Map<string, Contact>>(new Map());
  const [localContactStatuses, setLocalContactStatuses] = useState<ContactStepStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragOverStepId, setDragOverStepId] = useState<string | null>(null);
  const [draggingContact, setDraggingContact] = useState<string | null>(null);

  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const contactIds = engagement.contacts.map(c => c.contact_id);
        const { data, error } = await supabase
          .from('nrm_contacts')
          .select('*')
          .in('id', contactIds);

        if (error) throw error;

        const newContactsMap = new Map<string, Contact>();
        data.forEach(contact => {
          newContactsMap.set(contact.id, contact);
        });
        setContactsMap(newContactsMap);
      } catch (err) {
        console.error('Failed to fetch contact details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, [engagement.contacts]);

  // Keep local state in sync with engagement props
  useEffect(() => {
    console.log('Engagement updated:', {
      id: engagement.id,
      contactsCount: engagement.contacts?.length || 0,
      stepsCount: engagement.steps?.length || 0,
      statusCount: engagement.contactStatuses?.length || 0
    });
    setLocalContactStatuses(engagement.contactStatuses || []);
  }, [engagement]);

  // Get contacts at each step using local statuses
  const contactsByStep = React.useMemo(() => {
    const stepMap = new Map<string | null, Contact[]>();
    
    // Initialize with empty arrays for each step
    stepMap.set(null, []); // For contacts not yet started
    engagement.steps.forEach(step => {
      stepMap.set(step.id, []);
    });

    // Place each contact in their current step based on local statuses
    engagement.contacts.forEach(contactWrapper => {
      // Find all statuses for this contact
      const contactStatuses = localContactStatuses
        .filter(status => status.contact_id === contactWrapper.contact_id)
        .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime());

      // Get the latest status
      const latestStatus = contactStatuses[0];
      const stepId = latestStatus?.step_id || null;
      
      const contact = contactsMap.get(contactWrapper.contact_id);
      if (contact) {
        const currentContacts = stepMap.get(stepId) || [];
        currentContacts.push(contact);
        stepMap.set(stepId, currentContacts);
      }
    });

    return stepMap;
  }, [engagement.contacts, engagement.steps, localContactStatuses, contactsMap]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, stepId: string | null) => {
    e.preventDefault();
    setDragOverStepId(stepId);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverStepId(null);
    }
  };

  const handleDragStart = (contactId: string) => {
    setDraggingContact(contactId);
  };

  const handleDragEnd = () => {
    setDraggingContact(null);
    setDragOverStepId(null);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, toStepId: string | null) => {
    e.preventDefault();
    setDragOverStepId(null);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { contactId, currentStepId } = data;
      
      console.log('Handling drop in ExecutingEngagementView:', {
        contactId,
        currentStepId,
        toStepId,
        engagementId: engagement.id
      });
      
      if (currentStepId === toStepId) {
        console.log('Contact already in target step, skipping');
        return;
      }

      // Optimistically update local state
      const timestamp = new Date().toISOString();
      
      setLocalContactStatuses(prevStatuses => {
        // Remove any existing status for this contact
        const filteredStatuses = prevStatuses.filter(
          status => status.contact_id !== contactId
        );
        
        // Only add new status if moving to a step (not to "Not Started")
        if (toStepId === null) {
          return filteredStatuses;
        }

        // Create new status with proper typing
        const optimisticStatus: ContactStepStatus = {
          contact_id: contactId,
          engagement_id: engagement.id,
          step_id: toStepId,
          completed_at: timestamp
        };

        return [...filteredStatuses, optimisticStatus];
      });

      // Update server and handle response
      try {
        const updatedEngagement = await onMoveContact(contactId, currentStepId, toStepId);
        console.log('Contact move completed on server');
        
        // Update local state with server response
        setLocalContactStatuses(updatedEngagement.contactStatuses);
      } catch (err) {
        console.error('Failed to update server, reverting local state:', err);
        // Revert to previous state if server update fails
        setLocalContactStatuses(engagement.contactStatuses);
      }
    } catch (err) {
      console.error('Failed to process drop:', err);
    }
  };

  if (loading) {
    return <div className="p-4 text-gray-400">Loading contacts...</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {/* Not Started Column */}
      <div 
        className={`bg-dark-800 rounded-lg p-4 transition-all duration-200 ${
          dragOverStepId === null ? 'ring-2 ring-primary-500 ring-opacity-50' : ''
        }`}
        onDragOver={(e) => handleDragOver(e, null)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, null)}
      >
        <h3 className="text-lg font-medium text-white mb-4">Not Started</h3>
        <div className="space-y-4">
          {contactsByStep.get(null)?.map(contact => (
            <div key={contact.id}>
              <ContactCard 
                contact={contact}
                currentStepId={null}
                draggable={true}
                isDragging={draggingContact === contact.id}
                onDragStart={() => handleDragStart(contact.id)}
                onDragEnd={handleDragEnd}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Step Columns */}
      {engagement.steps.map((step) => (
        <div 
          key={step.id} 
          className={`bg-dark-800 rounded-lg p-4 transition-all duration-200 ${
            dragOverStepId === step.id ? 'ring-2 ring-primary-500 ring-opacity-50' : ''
          }`}
          onDragOver={(e) => handleDragOver(e, step.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, step.id)}
        >
          <h3 className="text-lg font-medium text-white mb-4">{step.name}</h3>
          <div className="space-y-4">
            {contactsByStep.get(step.id)?.map(contact => (
              <div key={contact.id}>
                <ContactCard 
                  contact={contact}
                  currentStepId={step.id}
                  draggable={true}
                  isDragging={draggingContact === contact.id}
                  onDragStart={() => handleDragStart(contact.id)}
                  onDragEnd={handleDragEnd}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
